import { MFAEmailIcon } from 'app-v2/assets/icons'
import { Button, colors, fonts, H7, Spacer } from 'app-v2/design-systems'
import { BORDER, COLOR, SIZE, SPACING } from 'app-v2/design-tokens'
import { SplitScreenLayout } from 'app/common/layout'
import { SmallText, Text } from 'app/common/typography'
import React, { useRef, useState } from 'react'
//$FlowFixMe
import ReactCodeInput from 'react-code-input'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { useSendCodeMutation, useVerifyCodeMutation } from './useMFAMutations'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import type { RootState } from 'shared/store/reducers'
import { featureCodes, messages } from 'shared/utils'
import { signInSuccess } from 'shared/store/signin'
import { isMobile } from 'react-device-detect'

const getErrorKey = (errorCode: string) => {
    switch (errorCode) {
        case messages.EXPIRED_CREDENTIALS:
            return 'signin.mfa.expired.credentials'
        case messages.WRONG_CREDENTIALS:
            return 'signin.mfa.wrong.credentials'
        default:
            return 'form.error.default'
    }
}
export const MFAConfirmCode = () => {
    const intl = useIntl()
    const { mfaToken, login } = useSelector(state => state.signinPageState)

    const newOrderFlowEnabled = useSelector((s: RootState) =>
        s.featuresState.available.includes(featureCodes.ENABLE_NEW_ORDER_FLOW)
    )

    const history = useHistory()
    const [code, setCode] = useState()
    const [error, setError] = useState()
    const [successKey, setSuccessKey] = useState()
    const dispatch = useDispatch()
    const location = useLocation()
    const inputRef = useRef()

    const {
        mutate: verifyCodeMutation,
        isLoading: isVerifyingCode
    } = useVerifyCodeMutation({
        onSuccess: response => {
            const { data, headers } = response
            dispatch(signInSuccess({ headers, fromEcommerce: false }))
            const newLocation =
                location?.state?.redirectTo ||
                intl.formatMessage({
                    id: 'routes.kingdom.dashboard'
                })
            if (newOrderFlowEnabled) {
                window.location.href = newLocation
            } else {
                history.push(newLocation)
            }
        },
        onError: error => {
            setError(error?.response?.data?.code)
        }
    })

    const resetMessages = () => {
        setError(undefined)
        setSuccessKey(undefined)
    }

    const {
        mutate: sendCodeMutation,
        isLoading: isSendingNewCode
    } = useSendCodeMutation({
        onSuccess: response => {
            setSuccessKey('signin.mfa.new.code.sent')
        },
        onError: error => {
            setError(error?.response?.data?.code)
        }
    })

    const handleCodeChange = newCode => {
        setCode(newCode)
    }

    const handleVerifyAuthCode = () => {
        resetMessages()
        verifyCodeMutation({ mfaToken, code })
    }

    const handleSendNewLinkClick = () => {
        resetMessages()
        // Workaround to clean input value
        inputRef.current.state.input = ['', '', '', '', '', '']
        setCode(undefined)
        sendCodeMutation(mfaToken, { email: login, mfaToken })
    }

    const handleReconnectRedirection = () => {
        history.push(
            intl.formatMessage({
                id: 'routes.signin'
            })
        )
    }

    const isLoading = isSendingNewCode || isVerifyingCode
    const isExpired = error === messages.EXPIRED_CREDENTIALS
    const wrongCredentials = error === messages.WRONG_CREDENTIALS
    const hasError = isExpired || wrongCredentials

    return (
        <SplitScreenLayout
            breadcrumbKey="signin.breadcrumb.end"
            titleKey="signin.title"
            baselineKey="signin.baseline"
            leftBlock={
                <StyledContainer>
                    <StyledMainIconContainer>
                        <StyledLockIconContainer>
                            <MFAEmailIcon />
                        </StyledLockIconContainer>
                    </StyledMainIconContainer>

                    <Spacer size={SIZE.XXL} />

                    <H7>
                        {intl.formatMessage({ id: 'signin.mfa.enter.code' })}
                    </H7>

                    <Spacer />

                    <Text>
                        {intl.formatMessage({
                            id: 'signin.mfa.enter.code.details'
                        })}
                    </Text>
                    <Text font={fonts.flameBold}>{login}</Text>

                    <Spacer size={SIZE.M} />

                    <StyledAuthFormContainer>
                        <StyledAuthCodeContainer>
                            <ReactCodeInput
                                type="text"
                                fields={6}
                                ref={inputRef}
                                value={code}
                                onChange={handleCodeChange}
                                inputStyle={{
                                    boxShadow: 'none',
                                    width: isMobile ? '40px' : '48px',
                                    height: isMobile ? '40px' : '48px',
                                    margin: isMobile
                                        ? SPACING.XXXS
                                        : SPACING.XXS,
                                    border: hasError
                                        ? `1px solid ${colors.fieryRed}`
                                        : `1px solid ${colors.lightGrey}`,
                                    padding: SPACING.XS,
                                    color: hasError
                                        ? colors.fieryRed
                                        : colors.bbqBrown,
                                    textAlign: 'center',
                                    fontFamily: fonts.flameSansRegular,
                                    borderRadius: BORDER.RADIUS.M
                                }}
                            />
                            {hasError && (
                                <SmallText color={colors.fieryRed}>
                                    {intl.formatMessage({
                                        id: getErrorKey(error)
                                    })}
                                </SmallText>
                            )}

                            {successKey && (
                                <SmallText color={colors.crunchyGreen}>
                                    {intl.formatMessage({
                                        id: successKey
                                    })}
                                </SmallText>
                            )}

                            <Spacer size={SIZE.XS} />

                            {isExpired ? (
                                <Button
                                    fullLength
                                    variant="contained"
                                    disabled={isLoading}
                                    buttonColor={
                                        isLoading
                                            ? colors.disable
                                            : COLOR.PRIMARY
                                    }
                                    labelColor={colors.white}
                                    onClick={handleReconnectRedirection}>
                                    {intl.formatMessage({
                                        id: 'signin.mfa.enter.reconnect.button'
                                    })}
                                </Button>
                            ) : (
                                <Button
                                    fullLength
                                    variant="contained"
                                    disabled={isLoading}
                                    buttonColor={
                                        isLoading
                                            ? colors.disable
                                            : COLOR.PRIMARY
                                    }
                                    labelColor={colors.white}
                                    onClick={handleVerifyAuthCode}>
                                    {intl.formatMessage({
                                        id:
                                            'signin.mfa.enter.verify.code.button'
                                    })}
                                </Button>
                            )}
                        </StyledAuthCodeContainer>
                    </StyledAuthFormContainer>

                    {!isExpired && (
                        <>
                            <Spacer size={SIZE.XL} />

                            <StyledTextContainer>
                                <SmallText>
                                    {intl.formatMessage({
                                        id: 'signin.mfa.enter.code.no.code'
                                    })}
                                </SmallText>

                                <Spacer />

                                <StyledLink onClick={handleSendNewLinkClick}>
                                    {intl.formatMessage({
                                        id: 'signin.mfa.enter.send.new.code'
                                    })}
                                </StyledLink>
                            </StyledTextContainer>
                        </>
                    )}
                </StyledContainer>
            }
            className="signin"
            noBackground
        />
    )
}

const StyledAuthFormContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`

const StyledAuthCodeContainer = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    max-width: 400px;
`
const StyledMainIconContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
`
const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
`

const StyledLockIconContainer = styled.div`
    position: relative;
    width: 120px;
`

const StyledLink = styled(SmallText)`
    cursor: pointer;
    text-decoration: underline;
`

const StyledTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    justify-content: center;
`
